import React, { useEffect, useMemo, useRef, useState } from "react";
import { format, parseISO } from "date-fns";
import {
  ActivityUserRow,
  ActivityUsersResponse,
  getActivityUsers,
} from "../../api/analyticsAdmin";
import { formatInteger } from "../../helpers/formatNumber";
import { Loader } from "../Loader";
import { AnalyticsFilters } from "./AnalyticsFilterBar";
import { UserJourneyModal } from "./UserJourneyModal";
import { usePersonIndex } from "./usePersonIndex";
import { resolveDistinctIds } from "../../helpers/analyticsPlusJourney";

const PAGE_SIZE = 25;

const UsersTab: React.FC<{ filters: AnalyticsFilters }> = ({ filters }) => {
  const [pageNumber, setPageNumber] = useState(1);
  const [data, setData] = useState<ActivityUsersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRow, setSelectedRow] = useState<ActivityUserRow | null>(null);

  const { index: personIndex, building: indexBuilding, truncated: indexTruncated } =
    usePersonIndex(filters);

  const selectedDistinctIds = useMemo(
    () =>
      selectedRow
        ? resolveDistinctIds(selectedRow.personId, selectedRow.distinctId, personIndex)
        : [],
    [selectedRow, personIndex]
  );

  // Reset to page 1 whenever the shared filters change. Done during render
  // (not in an effect) so the fetch effect below sees pageNumber=1 on the same
  // pass — avoids a wasted fetch of the old page before a separate reset effect
  // could run.
  const prevFiltersRef = useRef(filters);
  if (prevFiltersRef.current !== filters) {
    prevFiltersRef.current = filters;
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
  }

  useEffect(() => {
    let cancelled = false;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getActivityUsers({
          from: filters.from,
          to: filters.to,
          platform: filters.platform,
          audience: filters.audience,
          pagination: { pageNumber, pageSize: PAGE_SIZE },
        });
        if (!cancelled) setData(response);
      } catch {
        if (!cancelled) setError("Failed to load users.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => {
      cancelled = true;
    };
  }, [filters, pageNumber]);

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div>
      {loading ? (
        <Loader className="w-8 h-8" />
      ) : error ? (
        <div className="text-sm text-red-500">{error}</div>
      ) : !data || data.data.length === 0 ? (
        <div className="text-sm text-gray-500">No unique visitors for this range.</div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="text-left font-medium px-4 py-3">Distinct ID</th>
                  <th className="text-left font-medium px-4 py-3">Person ID</th>
                  <th className="text-right font-medium px-4 py-3">Events</th>
                  <th className="text-left font-medium px-4 py-3">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((row) => (
                  <tr
                    key={row.distinctId}
                    onClick={() => setSelectedRow(row)}
                    className="border-t border-gray-100 hover:bg-gray-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-mono text-xs">{row.distinctId}</td>
                    <td className="px-4 py-3 font-mono text-xs text-gray-500">
                      {row.personId ?? "—"}
                    </td>
                    <td className="px-4 py-3 text-right">{formatInteger(row.eventCount)}</td>
                    <td className="px-4 py-3">
                      {format(parseISO(row.lastSeen), "yyyy-MM-dd HH:mm")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between mt-4 text-sm text-gray-600">
            <span>{formatInteger(data.total)} users</span>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPageNumber((p) => Math.max(1, p - 1))}
                disabled={pageNumber <= 1}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Prev
              </button>
              <span>
                Page {pageNumber} / {totalPages}
              </span>
              <button
                onClick={() => setPageNumber((p) => Math.min(totalPages, p + 1))}
                disabled={pageNumber >= totalPages}
                className="px-3 py-1 border border-gray-300 rounded disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        </>
      )}

      <UserJourneyModal
        distinctId={selectedRow?.distinctId ?? null}
        personId={selectedRow?.personId ?? null}
        distinctIds={selectedDistinctIds}
        from={filters.from}
        to={filters.to}
        building={indexBuilding}
        truncated={indexTruncated}
        onClose={() => setSelectedRow(null)}
      />
    </div>
  );
};

export default UsersTab;
